import {StructuredTableToTableMetadata, Table} from 'notebook/tables/constants/TableConstants';
import {TableIndex} from 'notebook/tables/constants/TableSelectionConstants';
import TableUtil from 'notebook/tables/TableUtil';

/**
 * The first cell index of the table [0, 0].
 */
export function computeFirstIndex(): TableIndex {
  return [0, 0];
}

/**
 * The last cell index of the table [numRows - 1, numCols - 1].
 * Reflects actual data bounds, not ghost cells.
 */
export function computeLastIndex(table: Table): TableIndex {
  return [table.table.length - 1, table.table[0].length - 1];
}

/** Whether `rowIndex` is the last row of the table. */
export function computeIsLastRow(rowIndex: number, lastIndex: TableIndex): boolean {
  return rowIndex === lastIndex[0];
}

/** Whether `columnIndex` is the last column of the table. */
export function computeIsLastColumn(columnIndex: number, lastIndex: TableIndex): boolean {
  return columnIndex === lastIndex[1];
}

/**
 * Computes whether the table dimensions can be modified.
 * Returns false when the table has ANY locked dimension.
 *
 * Consider using computeCanModifyColumnDim or computeCanModifyRowDim instead for specificity.
 *
 * @param table - The table to check
 * @param viewer - Whether the table is in viewer mode (read-only context like print preview)
 * @param locked - Whether the table is locked for editing
 */
export function computeCanModifyDims(table: Table, viewer: boolean, locked: boolean): boolean {
  if (viewer) {
    return false;
  }
  if (locked) {
    return false;
  }
  if (TableUtil.hasLockedDims(table)) {
    return false;
  }
  return true;
}

/**
 * Computes whether the table's horizontal (column) dimension can be modified.
 * Returns true when columns can be added, removed, or reordered.
 *
 * @param table - The table to check
 * @param viewer - Whether the table is in viewer mode
 * @param locked - Whether the table is locked for editing
 */
export function computeCanModifyColumnDim(table: Table, viewer: boolean, locked: boolean): boolean {
  if (viewer) {
    return false;
  }
  if (locked) {
    return false;
  }
  if (TableUtil.crossCompatibleLockedColumnDimCheck(table.lockedDims)) {
    return false;
  }
  return true;
}

/**
 * Computes whether the table's vertical (row) dimension can be modified.
 * Returns true when rows can be added or removed.
 *
 * @param table - The table to check
 * @param viewer - Whether the table is in viewer mode
 * @param locked - Whether the table is locked for editing
 */
export function computeCanModifyRowDim(table: Table, viewer: boolean, locked: boolean): boolean {
  if (viewer) {
    return false;
  }
  if (locked) {
    return false;
  }
  if (TableUtil.crossCompatibleLockedRowDimCheck(table.lockedDims)) {
    return false;
  }
  return true;
}

/**
 * Computes whether rows can be removed from the table.
 * Takes into account viewer mode, table lock state, placeholder rows, and table settings.
 *
 * Note: The original TableEditable.canRemoveRows() also checked for a deleteRows handler,
 * but this is always provided by useTableMutations via tableOps, so it's not needed here.
 *
 * @param table - The table to check
 * @param viewer - Whether the table is in viewer mode
 * @param locked - Whether the table is locked for editing
 * @param hasPlaceholderRows - Whether the table has placeholder rows (which prevents deletion)
 */
export function computeCanRemoveRows(
  table: Table,
  viewer: boolean,
  locked: boolean,
  hasPlaceholderRows: boolean
): boolean {
  if (viewer) {
    return false;
  }
  if (locked) {
    return false;
  }
  if (hasPlaceholderRows) {
    return false;
  }
  const hasLockedDims = TableUtil.crossCompatibleLockedDimsCheck(table.lockedDims);
  if (hasLockedDims && !table.allowRowRemoval) {
    return false;
  }
  return true;
}

/**
 * Computes whether the table has a locked row count.
 * This is determined by the table metadata's lockRowCount property,
 * but is ignored for config tables.
 *
 * @param table - The table to check
 * @param isConfigTable - Whether the table is in config mode (templates)
 */
export function computeHasLockedRowCount(table: Table, isConfigTable: boolean): boolean {
  const metadata = table.metadata as StructuredTableToTableMetadata<unknown> | undefined;
  return !isConfigTable && (metadata?.metadata?.lockRowCount ?? false);
}

/**
 * Computes whether ghost cells (empty placeholder columns to the right of real data) should
 * be displayed. Ghost cells appear when the table allows freeform column additions AND either
 * there is an active multi-cell selection or the table is maximized/split-screen.
 */
export function computeShouldShowGhostCells(params: {
  canModifyColumnDim: boolean;
  canAddRemoveColumns: boolean;
  canAddColumnsFreeform: boolean;
  hasMultiSelection: boolean;
  isMaximizedOrSplitScreen: boolean;
}): boolean {
  if (!params.canModifyColumnDim || !params.canAddRemoveColumns || !params.canAddColumnsFreeform) {
    return false;
  }
  return params.hasMultiSelection || params.isMaximizedOrSplitScreen;
}

/**
 * Computes the number of ghost cells (empty placeholder columns) to render to the right of
 * the table's real data columns. Returns 0 when ghost cells should not be shown.
 *
 * When ghost cells are shown, the total column count is clamped between
 * TABLE_MIN_COLUMNS_WITH_GHOSTS and TABLE_MAX_COLUMNS, with at least
 * TABLE_EXTRA_GHOST_COLUMN_COUNT additional columns beyond the actual data.
 */
export function computeGhostCellsCount(table: Table, shouldShowGhostCells: boolean): number {
  return TableUtil.getColumnCount(table, shouldShowGhostCells) - table.table[0].length;
}

/**
 * Computes whether rows can be added to the table.
 * Takes into account viewer mode, table lock state, table settings,
 * and locked row count metadata.
 *
 * @param table - The table to check
 * @param viewer - Whether the table is in viewer mode
 * @param locked - Whether the table is locked for editing
 * @param isConfigTable - Whether the table is in config mode (templates)
 */
export function computeCanAddRows(
  table: Table,
  viewer: boolean,
  locked: boolean,
  isConfigTable: boolean
): boolean {
  const hasLockedRowCount = computeHasLockedRowCount(table, isConfigTable);
  if (viewer) {
    return false;
  }
  if (locked) {
    return false;
  }
  if (!TableUtil.allowAddRows(table)) {
    return false;
  }
  return !hasLockedRowCount;
}
